/**
 * SectionEditOverlay — the 2D editing layer drawn over a locked surface.
 *
 * An SVG laid exactly over the 3D view (the viewer supplies the projection from
 * surface feet to container pixels). It shows everything placed on the surface
 * — product sections, and windows and doors — and lets each be worked on the
 * same way:
 *   - an outline — click to select, Shift- or Ctrl-click to add it to (or take
 *     it out of) the selection;
 *   - when exactly ONE thing is selected: eight resize handles, drag-to-move,
 *     a delete chip, and each of its edges coloured by what it meets, with the
 *     trim it carries on hover. Click an edge to choose its trim. Several
 *     selected things are only outlined — they are arranged with align /
 *     distribute, not dragged;
 *   - a "+" on every bare area big enough to take a new section;
 *   - snap guides while dragging, the safe-margin line while an opening is
 *     selected, and the surface's overall dimensions.
 *
 * It holds only the drag in progress. All geometry — snapping, staying on the
 * surface, SlatWall rows, doors staying on the floor, what may overlap what —
 * lives in `calculations/sections` (dragMath / openings / validation), shared
 * with every other way of editing, so a drag simply stops at anything it may
 * not cross. A finished drag is reported once, on release; Escape abandons it.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EdgeSide, Opening, Section, SlatWallAccessory, Surface } from '../../types/sections';
import { accessoryDef } from '../../data/slatwallAccessories';
import { SurfaceProjection } from '../ui/room3d/types';
import { formatDimension } from '../ui/room3d/dimensionLabels';
import {
  Rect,
  Handle,
  DragFrame,
  DragResult,
  OpeningRules,
  resolveDrag,
  resolveOpeningDrag,
  snapTargetsFor,
  handleEdges,
  computeGaps,
  sectionTrims,
  openingTrims,
  TrimRun,
  findTrimProduct,
  surfaceAccessories,
  resolveAccessoryMove,
} from '../../calculations/sections';
// Moved to utils/dimensions (the results page needs it without the designer); re-exported for the planner's imports.
import { formatFeetInches } from '../../utils/dimensions';
import { PRODUCT_TYPE_LABEL } from './sectionOptions';
import { DESIGNER_STRINGS as S } from './strings';
import './designer.css';
export { formatFeetInches };

const SNAP_PX = 14;            // screen-distance snap tolerance (wider = more "magnetic")
const HANDLE_PX = 10;          // visible handle square
const HANDLE_HIT_PX = 28;      // invisible grab area behind it (finger-sized)
const CHIP_R = 11;             // add chip radius
const DELETE_R = 9;            // delete chip radius: a little smaller, tucked just off the item's corner

const AMBER = '#FEB100';
const OPENING_STROKE = '#b48cff';

/** Edge colours by what the edge meets (see calculations/sections/adjacency). */
export const EDGE_COLORS = { perimeter: '#00b8d9', section: '#3ddc84', opening: '#b48cff', bare: '#ff7a59', covered: 'rgba(255,255,255,0.4)' } as const;

const CURSOR_BY_HANDLE: Record<Handle, string> = {
  nw: 'nwse-resize',
  n: 'ns-resize',
  ne: 'nesw-resize',
  e: 'ew-resize',
  se: 'nwse-resize',
  s: 'ns-resize',
  sw: 'nesw-resize',
  w: 'ew-resize',
  move: 'move',
};

export interface SectionEditOverlayProps {
  surface: Surface;
  selectedIds: string[];
  projection: SurfaceProjection;
  /** Safe margin for openings (feet). */
  openingRules: OpeningRules;
  /** `additive` — Shift / Ctrl / Cmd was held: toggle this item in the selection. */
  onSelect: (id: string, additive: boolean) => void;
  /** A drag finished: the item's new rectangle (already validated). */
  onCommitRect: (id: string, rect: Rect) => void;
  onDelete?: (id: string) => void;
  /** The "+" on a bare area was clicked; `at` is the centre of the "+" in viewer px (for a menu to open on). */
  onAddAt?: (gap: Rect, at: { x: number; y: number }) => void;
  /** An edge of the selected item was clicked, at this point in the viewer (px). */
  onEdgeClick?: (id: string, side: EdgeSide, at: { x: number; y: number }) => void;
  /** A SlatWall accessory was dragged: its new place on its section (section-relative feet, already resolved). */
  onCommitAccessory?: (id: string, x: number, y: number) => void;
  /**
   * When true, draws a thin light-gray line around the surface's actual extents.
   * Only useful when the 3D framing is hidden — otherwise the studs themselves
   * indicate where the wall ends.
   */
  framingHidden?: boolean;
}

/** Anything placed on the surface, as the overlay handles it. */
type Item =
  | { id: string; rect: Rect; kind: 'section'; section: Section }
  | { id: string; rect: Rect; kind: 'opening'; opening: Opening }
  /** A SlatWall accessory: moved as a whole (no resize), kept on its section. */
  | { id: string; rect: Rect; kind: 'accessory'; section: Section; accessory: SlatWallAccessory };

interface DragState {
  item: Item;
  handle: Handle;
  startClient: { x: number; y: number };
  origRect: Rect;
  draftRect: Rect;
}

const rectOf = (s: { x: number; y: number; width: number; height: number }): Rect => ({ x: s.x, y: s.y, width: s.width, height: s.height });

/** "Top · 6' — meets SlatWall (Gray) · Snap-In H Trim 10' Gray" */
function describeEdge(seg: TrimRun, kind: Surface['kind']): string {
  const n = seg.neighbour;
  let meets: string;
  if (n.kind === 'perimeter') meets = kind === 'ceiling' ? S.edgePerimeterCeiling : S.edgePerimeterWall[n.side];
  else if (n.kind === 'bare') meets = S.edgeBare;
  else if (n.kind === 'covered') meets = S.edgeCovered;
  else if (n.kind === 'opening') meets = S.openingName[n.openingKind].toLowerCase();
  else {
    const inches = Math.round(n.gap * 12);
    meets = `${PRODUCT_TYPE_LABEL[n.productType]} (${n.color})${inches > 0 ? S.edgeGap(inches) : ''}`;
  }
  const trim = seg.trim
    ? `${findTrimProduct(seg.trim, seg.color)?.name ?? seg.trim}${seg.source === 'override' ? S.trimChosen : ''}`
    : S.trimNone[seg.none ?? 'override'];
  const where = `${S.edgeSide[seg.side]} · ${formatFeetInches(seg.length)} — ${n.kind === 'covered' ? meets : `meets ${meets}`}`;
  return n.kind === 'covered' ? where : `${where} · ${trim}`;
}

export const SectionEditOverlay: React.FC<SectionEditOverlayProps> = ({
  surface,
  selectedIds,
  projection,
  openingRules,
  onSelect,
  onCommitRect,
  onDelete,
  onAddAt,
  onEdgeClick,
  onCommitAccessory,
  framingHidden = false,
}) => {
  const [drag, setDrag] = useState<DragState | null>(null);

  // ---------- Projection helpers ----------
  const proj = projection;
  const ftToPx = useCallback((sx: number, sy: number) => ({
    x: proj.originPx.x + sx * proj.axisX.x + sy * proj.axisY.x,
    y: proj.originPx.y + sx * proj.axisX.y + sy * proj.axisY.y,
  }), [proj]);

  const pxDeltaToFt = useCallback((dx: number, dy: number) => {
    const ax = proj.axisX.x, ay = proj.axisX.y, bx = proj.axisY.x, by = proj.axisY.y;
    const det = ax * by - ay * bx;
    if (Math.abs(det) < 1e-9) return { sx: 0, sy: 0 };
    return { sx: (dx * by - dy * bx) / det, sy: (-dx * ay + dy * ax) / det };
  }, [proj]);

  // How the surface is drawn on screen. Left / right walls are seen from inside
  // the room, so their +x runs screen-LEFT; +y normally runs screen-UP (px y
  // decreasing). dragMath needs both to know which edge a screen handle moves.
  const orientation = useMemo(() => ({
    xFlipped: proj.axisX.x < 0,
    yFlipped: proj.axisY.y > 0,
  }), [proj]);

  const snapTolFt = useMemo(() => {
    const pxPerFt = Math.max(0.001, Math.hypot(proj.axisX.x, proj.axisX.y));
    return SNAP_PX / pxPerFt;
  }, [proj]);

  // Everything on the surface, bottom layer first: boards, then SlatWall, then
  // openings — so the thing on top is the one a click lands on.
  const items = useMemo<Item[]>(() => [
    ...surface.sections
      .slice()
      .sort((a, b) => (a.productType === 'SlatWall' ? 1 : 0) - (b.productType === 'SlatWall' ? 1 : 0))
      .map((section): Item => ({ id: section.id, rect: rectOf(section), kind: 'section', section })),
    ...(surface.openings ?? []).map((opening): Item => ({ id: opening.id, rect: rectOf(opening), kind: 'opening', opening })),
    // Accessories on top of their SlatWall, so a click lands on the hook, not the slats under it.
    ...surfaceAccessories(surface).map(({ section, accessory, rect }): Item => ({ id: accessory.id, rect, kind: 'accessory', section, accessory })),
  ], [surface]);

  // Rebuilt only when a different item is picked up (not on every pointer move).
  // An opening also snaps to the safe-margin lines.
  const dragId = drag ? drag.item.id : null;
  const dragIsOpening = drag ? drag.item.kind === 'opening' : false;
  const dragFrame = useMemo<DragFrame | null>(() => (dragId ? {
    ...orientation,
    snapTolFt,
    targets: snapTargetsFor(surface, dragId, dragIsOpening ? openingRules.margin : 0),
  } : null), [dragId, dragIsOpening, orientation, snapTolFt, surface, openingRules.margin]);

  // ---------- Drag lifecycle (window listeners while dragging) ----------
  useEffect(() => {
    if (!drag || !dragFrame) return;

    const onMove = (e: PointerEvent) => {
      const { sx, sy } = pxDeltaToFt(e.clientX - drag.startClient.x, e.clientY - drag.startClient.y);
      if (drag.item.kind === 'accessory') {
        // An accessory moves as a whole, stays on its section, and its top
        // snaps to a slot; a spot that is taken is simply not taken.
        const { section, accessory } = drag.item;
        const to = resolveAccessoryMove(surface, section, accessory, sx, sy);
        if (to) setDrag((d) => (d ? { ...d, draftRect: { ...d.origRect, x: section.x + to.x, y: section.y + to.y } } : null));
        return;
      }
      const result: DragResult = drag.item.kind === 'section'
        ? resolveDrag(surface, drag.item.section, drag.handle, drag.origRect, sx, sy, dragFrame)
        : resolveOpeningDrag(surface, drag.item.opening, drag.handle, drag.origRect, sx, sy, dragFrame, openingRules);
      // Refused positions are simply not taken: the item stays at the last
      // place it was allowed to be.
      if (result.ok) setDrag((d) => (d ? { ...d, draftRect: result.rect } : null));
    };

    const onUp = () => {
      const o = drag.origRect, d = drag.draftRect;
      const changed = o.x !== d.x || o.y !== d.y || o.width !== d.width || o.height !== d.height;
      if (changed) {
        if (drag.item.kind === 'accessory') onCommitAccessory?.(drag.item.id, d.x - drag.item.section.x, d.y - drag.item.section.y);
        else onCommitRect(drag.item.id, d);
      }
      setDrag(null);
    };

    const onCancel = () => setDrag(null);

    // Capture phase + stopPropagation: Escape during a drag abandons the drag
    // and must not also reach the designer's own Escape handling.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setDrag(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [drag, dragFrame, surface, openingRules, pxDeltaToFt, onCommitRect, onCommitAccessory]);

  const startDrag = (item: Item, handle: Handle) => (e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.preventDefault();
    e.stopPropagation();
    setDrag({ item, handle, startClient: { x: e.clientX, y: e.clientY }, origRect: item.rect, draftRect: item.rect });
  };

  // Convert a surface-local rect into an SVG-aligned x/y/width/height in container px.
  const rectToSvg = (r: Rect) => {
    const bl = ftToPx(r.x, r.y);
    const tr = ftToPx(r.x + r.width, r.y + r.height);
    return { x: Math.min(bl.x, tr.x), y: Math.min(bl.y, tr.y), w: Math.abs(tr.x - bl.x), h: Math.abs(tr.y - bl.y) };
  };

  // Bare areas that could take a new section. Hidden during a drag (they would
  // be describing the layout from before it).
  const gaps = useMemo(() => (onAddAt ? computeGaps(surface) : []), [surface, onAddAt]);

  const wall = rectToSvg({ x: 0, y: 0, width: surface.width, height: surface.height });
  const sole = selectedIds.length === 1 ? items.find((i) => i.id === selectedIds[0]) : undefined;
  // The safe-margin line matters while an opening is being worked on.
  const showMargin = openingRules.margin > 0 && ((sole?.kind === 'opening') || dragIsOpening);

  // ---------- Render ----------
  return (
    <svg
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', touchAction: 'none' }}
    >
      {/* Surface perimeter + its overall dimensions: width along the bottom,
          height up the left. (The 3D view's own dimension callouts are hidden
          while a surface is locked.) */}
      {(() => {
        const cx = wall.x + wall.w / 2;
        const cy = wall.y + wall.h / 2;
        const bottom = wall.y + wall.h;
        const right = wall.x + wall.w;
        const off = 26;
        const color = '#00b8d9';
        return (
          <g>
            {(framingHidden || surface.outline) && (surface.outline ? (
              // A wall under a sloped ceiling: its real shape, not the rectangle it is stored as.
              <polygon points={surface.outline.map(([x, y]) => { const p = ftToPx(x, y); return `${p.x},${p.y}`; }).join(' ')} fill="none" stroke="rgba(160, 160, 160, 0.85)" strokeWidth={1} />
            ) : (
              <rect x={wall.x} y={wall.y} width={wall.w} height={wall.h} fill="none" stroke="rgba(160, 160, 160, 0.85)" strokeWidth={1} />
            ))}

            <line x1={wall.x} y1={bottom + off} x2={right} y2={bottom + off} stroke={color} strokeWidth={1} />
            <line x1={wall.x} y1={bottom + off - 5} x2={wall.x} y2={bottom + off + 5} stroke={color} strokeWidth={1} />
            <line x1={right} y1={bottom + off - 5} x2={right} y2={bottom + off + 5} stroke={color} strokeWidth={1} />
            <text x={cx} y={bottom + off + 18} textAnchor="middle" fontSize={14} fontWeight={700} fill={color}>{formatDimension(surface.width)}</text>

            <line x1={wall.x - off} y1={wall.y} x2={wall.x - off} y2={bottom} stroke={color} strokeWidth={1} />
            <line x1={wall.x - off - 5} y1={wall.y} x2={wall.x - off + 5} y2={wall.y} stroke={color} strokeWidth={1} />
            <line x1={wall.x - off - 5} y1={bottom} x2={wall.x - off + 5} y2={bottom} stroke={color} strokeWidth={1} />
            <text x={wall.x - off - 10} y={cy} textAnchor="middle" fontSize={14} fontWeight={700} fill={color} transform={`rotate(-90, ${wall.x - off - 10}, ${cy})`}>{formatDimension(surface.height)}</text>
          </g>
        );
      })()}

      {/* Safe margin: windows and doors stay inside this line (doors may reach the floor). */}
      {showMargin && (() => {
        const m = rectToSvg({ x: openingRules.margin, y: 0, width: surface.width - 2 * openingRules.margin, height: surface.height - openingRules.margin });
        const sill = ftToPx(0, openingRules.margin).y;
        return (
          <g>
            <title>{S.safeMargin}</title>
            <rect x={m.x} y={m.y} width={m.w} height={m.h} fill="none" stroke={OPENING_STROKE} strokeOpacity={0.7} strokeWidth={1} strokeDasharray="2 5" />
            <line x1={m.x} y1={sill} x2={m.x + m.w} y2={sill} stroke={OPENING_STROKE} strokeOpacity={0.45} strokeWidth={1} strokeDasharray="2 5" />
          </g>
        );
      })()}

      {/* Bare areas — dashed outline with a "+" to add a section there. Only
          the "+" takes clicks, so clicking the rest of a bare area still
          clears the selection. */}
      {!drag && gaps.map((gap, i) => {
        const g = rectToSvg(gap);
        const cx = g.x + g.w / 2;
        const cy = g.y + g.h / 2;
        return (
          <g key={`gap-${i}`}>
            <rect x={g.x + 2} y={g.y + 2} width={Math.max(0, g.w - 4)} height={Math.max(0, g.h - 4)} fill="rgba(254,177,0,0.06)" stroke="rgba(254,177,0,0.55)" strokeWidth={1} strokeDasharray="4 4" />
            <g
              pointerEvents="all"
              style={{ cursor: 'pointer' }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onAddAt?.(gap, { x: cx, y: cy }); }}
            >
              <title>{S.addSection}</title>
              <circle cx={cx} cy={cy} r={Math.max(CHIP_R + 3, HANDLE_HIT_PX / 2)} fill="transparent" />
              <circle cx={cx} cy={cy} r={CHIP_R + 3} fill="#1a1a1e" stroke={AMBER} strokeWidth={1.5} />
              <line x1={cx - 6} y1={cy} x2={cx + 6} y2={cy} stroke={AMBER} strokeWidth={2} strokeLinecap="round" />
              <line x1={cx} y1={cy - 6} x2={cx} y2={cy + 6} stroke={AMBER} strokeWidth={2} strokeLinecap="round" />
            </g>
          </g>
        );
      })}

      {/* Snap guides — amber dashed lines wherever a MOVING edge of the dragged
          item is sitting on the perimeter, a margin line or another item's edge. */}
      {drag && dragFrame && (() => {
        const tol = 1e-3;
        const edges = handleEdges(drag.handle, dragFrame);
        const dl = drag.draftRect.x;
        const dr = drag.draftRect.x + drag.draftRect.width;
        const db = drag.draftRect.y;
        const dt = drag.draftRect.y + drag.draftRect.height;
        const xSnaps = new Set<number>();
        const ySnaps = new Set<number>();
        for (const t of dragFrame.targets.xs) {
          if (edges.left && Math.abs(dl - t) < tol) xSnaps.add(t);
          if (edges.right && Math.abs(dr - t) < tol) xSnaps.add(t);
        }
        for (const t of dragFrame.targets.ys) {
          if (edges.bottom && Math.abs(db - t) < tol) ySnaps.add(t);
          if (edges.top && Math.abs(dt - t) < tol) ySnaps.add(t);
        }
        const lines: React.ReactElement[] = [];
        xSnaps.forEach((xt) => {
          const x = ftToPx(xt, 0).x;
          lines.push(<line key={`xs-${xt}`} x1={x} y1={wall.y - 22} x2={x} y2={wall.y + wall.h + 22} stroke={AMBER} strokeWidth={2} strokeDasharray="6 4" />);
        });
        ySnaps.forEach((yt) => {
          const y = ftToPx(0, yt).y;
          lines.push(<line key={`ys-${yt}`} x1={wall.x - 22} y1={y} x2={wall.x + wall.w + 22} y2={y} stroke={AMBER} strokeWidth={2} strokeDasharray="6 4" />);
        });
        return <g>{lines}</g>;
      })()}

      {items.map((item) => {
        const isSelected = selectedIds.includes(item.id);
        // Handles, dragging and the delete chip belong to a lone selection.
        const isSole = isSelected && selectedIds.length === 1;
        const isDragging = drag?.item.id === item.id;
        const liveRect: Rect = isDragging ? drag.draftRect : item.rect;
        const svg = rectToSvg(liveRect);
        const isOpening = item.kind === 'opening';
        const isAccessory = item.kind === 'accessory';
        // Nothing that isn't selected gets an outline or a tint — the overlay
        // must not draw on the picture under it (a section's outline ran as a
        // white line across any door set into it). It outlines on hover
        // (designer.css), so it still reads as something you can click.
        // (An accessory too: the hook itself shows where it is, and only the
        // selected one is outlined, so it stands out from the rest.)
        const idle = 'none';

        return (
          <g key={item.id}>
            <rect
              x={svg.x}
              y={svg.y}
              width={svg.w}
              height={svg.h}
              rx={isAccessory ? 3 : undefined}
              className={isSelected ? undefined : isOpening ? 'designer-opening-idle' : 'designer-section-idle'}
              fill="transparent"
              pointerEvents="all"
              stroke={isSelected ? AMBER : idle}
              strokeWidth={isSelected ? 2 : 1}
              strokeDasharray={isSelected ? '5 3' : undefined}
              style={{ cursor: isSole ? 'move' : 'pointer', touchAction: 'none' }}
              onClick={(e) => {
                e.stopPropagation();
                const additive = e.shiftKey || e.ctrlKey || e.metaKey;
                if (additive || !isSole) onSelect(item.id, additive);
              }}
              // A click on an accessory picks it up at once: selection and drag in one press.
              onPointerDown={(isSole || isAccessory) ? (e) => {
                if (e.shiftKey || e.ctrlKey || e.metaKey) return;
                if (!isSole) onSelect(item.id, false);
                startDrag(item, 'move')(e);
              } : undefined}
            >
              {isOpening && <title>{S.openingName[item.opening.kind]}</title>}
              {isAccessory && <title>{S.accessories.selected(accessoryDef(item.accessory.kind).label)}</title>}
            </rect>

            {/* Live size while dragging */}
            {isDragging && (
              <g pointerEvents="none">
                <rect x={svg.x + svg.w / 2 - 62} y={svg.y + svg.h / 2 - 13} width={124} height={26} rx={6} fill="rgba(26,26,30,0.88)" />
                <text x={svg.x + svg.w / 2} y={svg.y + svg.h / 2 + 5} textAnchor="middle" fontSize={13} fontWeight={700} fill="#fff">
                  {formatFeetInches(liveRect.width)} × {formatFeetInches(liveRect.height)}
                </text>
              </g>
            )}

            {/* What each edge meets and the trim it carries — one stroke per
                stretch: solid where there is trim, dashed where there is none.
                Click one to choose that side's trim. */}
            {isSole && !isDragging && item.kind !== 'accessory' && (item.kind === 'section' ? sectionTrims(surface, item.section) : openingTrims(surface, item.opening)).map((seg) => {
              const { x, y, width, height } = item.rect;
              const horiz = seg.side === 'top' || seg.side === 'bottom';
              const fixed = seg.side === 'top' ? y + height : seg.side === 'bottom' ? y : seg.side === 'right' ? x + width : x;
              const p0 = horiz ? ftToPx(seg.from, fixed) : ftToPx(fixed, seg.from);
              const p1 = horiz ? ftToPx(seg.to, fixed) : ftToPx(fixed, seg.to);
              const covered = seg.neighbour.kind === 'covered';
              const clickable = !!onEdgeClick && !covered;
              return (
                <g
                  key={`${seg.side}-${seg.from}`}
                  style={{ cursor: clickable ? 'pointer' : 'default' }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!clickable) return;
                    const box = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
                    onEdgeClick?.(item.id, seg.side, { x: e.clientX - box.left, y: e.clientY - box.top });
                  }}
                >
                  <title>{describeEdge(seg, surface.kind)}</title>
                  {/* generous invisible grab area */}
                  <line x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y} stroke="transparent" strokeWidth={16} pointerEvents="stroke" />
                  <line
                    x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y}
                    stroke={EDGE_COLORS[seg.neighbour.kind]}
                    strokeWidth={seg.source === 'override' ? 6 : seg.trim ? 4 : 2}
                    strokeDasharray={seg.trim ? undefined : '3 4'}
                    strokeLinecap="butt"
                    pointerEvents="none"
                  />
                </g>
              );
            })}

            {isSole && !isDragging && (() => {
              const cx = svg.x + svg.w / 2;
              const cy = svg.y + svg.h / 2;
              const handles: { handle: Handle; cx: number; cy: number }[] = [
                { handle: 'nw', cx: svg.x,          cy: svg.y          },
                { handle: 'n',  cx: cx,             cy: svg.y          },
                { handle: 'ne', cx: svg.x + svg.w,  cy: svg.y          },
                { handle: 'e',  cx: svg.x + svg.w,  cy: cy             },
                { handle: 'se', cx: svg.x + svg.w,  cy: svg.y + svg.h  },
                { handle: 's',  cx: cx,             cy: svg.y + svg.h  },
                { handle: 'sw', cx: svg.x,          cy: svg.y + svg.h  },
                { handle: 'w',  cx: svg.x,          cy: cy             },
              ];
              return (
                <>
                  {!isAccessory && handles.map((h) => (
                    <g
                      key={h.handle}
                      pointerEvents="all"
                      style={{ cursor: CURSOR_BY_HANDLE[h.handle], touchAction: 'none' }}
                      onPointerDown={startDrag(item, h.handle)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <rect x={h.cx - HANDLE_HIT_PX / 2} y={h.cy - HANDLE_HIT_PX / 2} width={HANDLE_HIT_PX} height={HANDLE_HIT_PX} fill="transparent" />
                      <rect x={h.cx - HANDLE_PX / 2} y={h.cy - HANDLE_PX / 2} width={HANDLE_PX} height={HANDLE_PX} fill={AMBER} stroke="#1a1a1e" strokeWidth={1} />
                    </g>
                  ))}

                  {/* Delete chip — just off the top-right corner (clear of the corner handle, when there is one). */}
                  {onDelete && (() => {
                    const off = isAccessory ? 4 : HANDLE_PX / 2 + 4;
                    const dx = svg.x + svg.w + DELETE_R + off;
                    const dy = svg.y - DELETE_R - off;
                    return (
                      <g
                        pointerEvents="all"
                        style={{ cursor: 'pointer' }}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                      >
                        <title>{isAccessory ? S.accessories.deleteOne : isOpening ? S.deleteOpening : S.deleteSection}</title>
                        <circle cx={dx} cy={dy} r={HANDLE_HIT_PX / 2} fill="transparent" />
                        <circle cx={dx} cy={dy} r={DELETE_R} fill="#c0392b" stroke="#fff" strokeWidth={1.5} />
                        <line x1={dx - 3.2} y1={dy - 3.2} x2={dx + 3.2} y2={dy + 3.2} stroke="#fff" strokeWidth={2} strokeLinecap="round" />
                        <line x1={dx + 3.2} y1={dy - 3.2} x2={dx - 3.2} y2={dy + 3.2} stroke="#fff" strokeWidth={2} strokeLinecap="round" />
                      </g>
                    );
                  })()}
                </>
              );
            })()}
          </g>
        );
      })}
    </svg>
  );
};

export default SectionEditOverlay;
